import { AlphaCard, Button, HorizontalGrid, HorizontalStack, Icon, Link, Modal, Text, TextField, VerticalStack } from "@shopify/polaris";
import { useState } from "react";
import { saveAddon, tryToDeleteAddon, tryToUpdateAddon } from "../firebase";
import renderIcon from '../utils/price-icons'

export default function AddonModal({
  addonModalStage,
  setAddonModalStage,
  open,
  setOpen,
  newAddon,
  setNewAddon,
}) {

  const [saveLoading, setSaveLoading] = useState(false);

  async function saveNewAddon() {
    try {
      setSaveLoading(true);
      await saveAddon(newAddon);
      setAddonModalStage(0);
      setNewAddon(null);
      setOpen(false);
    } catch (e) {
      console.error(e)
    } finally {
      setSaveLoading(false)
    }
  }

  function selectTemplate(type) {
    switch (type) {
      case 'postToAllPlatforms':
        setNewAddon({
          type,
          title: 'Post to All Platforms',
          piecesOfContent: 1,
          description: 'Post to all platforms',
          price: 100,
        });
        break;
      case 'postToOnePlatform':
        setNewAddon({
          type,
          title: 'Post to Tiktok',
          piecesOfContent: 1,
          description: 'Post to one platform',
          price: 100,
        });
        break;
      case 'fullRights':
        setNewAddon({
          type,
          title: 'Full Rights to Use',
          description: 'Full rights to use the content',
          price: 250,
        });
        break;
      case 'custom':
        setNewAddon({
          type,
          title: 'Custom Addon',
          description: 'Custom addon description',
          price: 100,
        });
        break;
      default:
        break;
    }
    setAddonModalStage(1);
  }


  let modalStageContent;
  if (addonModalStage === 0) {
    modalStageContent = (
      <VerticalStack gap="2">
        <Text variant="headingLg">Addon Templates</Text>
        <HorizontalGrid columns={{ xs: "1", md: "4" }} gap="6">
          <Link removeUnderline onClick={() => selectTemplate('postToAllPlatforms')}>
            <AlphaCard >
              <VerticalStack inlineAlign="center" gap="1">
                {renderIcon('30secMention', 'interactive')}
                <Text variant="headingSm">
                  Post to All Platforms
                </Text>
              </VerticalStack>
            </AlphaCard>
          </Link>
          <Link removeUnderline onClick={() => selectTemplate('postToOnePlatform')}>
            <AlphaCard >
              <VerticalStack inlineAlign="center" gap="1">
                {renderIcon('1minMention', 'interactive')}
                <Text variant="headingSm">
                  Post to One Platform
                </Text>
              </VerticalStack>
            </AlphaCard>
          </Link>
          <Link removeUnderline onClick={() => selectTemplate('fullRights')}>
            <AlphaCard >
              <VerticalStack inlineAlign="center" gap="1">
                {renderIcon('3shortBundle', 'interactive')}
                <Text variant="headingSm">
                  Full Rights to Use
                </Text>
              </VerticalStack>
            </AlphaCard>
          </Link>
          <Link removeUnderline onClick={() => selectTemplate('custom')}>
            <AlphaCard >
              <VerticalStack inlineAlign="center" gap="1">
                {renderIcon('youtubeShort', 'interactive')}
                <Text variant="headingSm">
                  Custom
                </Text>
              </VerticalStack>
            </AlphaCard>
          </Link>
        </HorizontalGrid>
      </VerticalStack>
    )
  } else if (addonModalStage === 1 || addonModalStage === 2) {
    modalStageContent = (
      <HorizontalGrid columns={{ xs: "1", md: "2" }} gap="6">
        <VerticalStack inlineAlign="center">
          <HorizontalStack>
            <AlphaCard background="bg-subdued">
              <VerticalStack gap="2" inlineAlign="center" >
                <Text variant="bodyLg">{newAddon.title}</Text>
                <Text color="success" variant="headingLg">+ ${Number(newAddon.price)?.toLocaleString()}</Text>
                <VerticalStack inlineAlign="start" gap="3">
                  <Text variant="bodyMd" color="subdued" >{newAddon.description}</Text>
                </VerticalStack>
              </VerticalStack>
            </AlphaCard>
          </HorizontalStack>
        </VerticalStack>
        <VerticalStack gap="4">
          <HorizontalGrid gap="2" columns="2">
            <TextField label="Title" value={newAddon.title} onChange={(value) => setNewAddon({ ...newAddon, title: value })} maxLength={30} />
            <TextField prefix="$" label="Price" value={newAddon.price} onChange={(value) => setNewAddon({ ...newAddon, price: value })} type="currency" maxLength={6} />
          </HorizontalGrid>
          <TextField
            label="Description"
            value={newAddon.description}
            onChange={(value) => setNewAddon({ ...newAddon, description: value })}
            maxLength={100}
          />
        </VerticalStack>
      </HorizontalGrid>
    )
  }

  const editPrimaryAction = {
    content: 'Save Changes',
    loading: saveLoading,
    disabled: !newAddon || newAddon.price > 100000 || newAddon.title === '' || newAddon.description === '',
    onAction: async () => {
      try {
        setSaveLoading(true)
        await tryToUpdateAddon(newAddon);
        setOpen(false)
        setAddonModalStage(0)
      } catch (e) {
        alert(e.message)
      } finally {
        setSaveLoading(false)
      }
    }
  }
  const editSecondaryActions = [
    {
      content: 'Cancel',
      onAction: () => {
        setOpen(false)
        setAddonModalStage(0)
      },
    },
    {
      content: 'Delete',
      destructive: true,
      loading: saveLoading,
      onAction: async () => {
        try {
          setSaveLoading(true)
          await tryToDeleteAddon(newAddon.id);
          setOpen(false)
          setAddonModalStage(0)
        } catch (e) {
          alert(e.message)
        } finally {
          setSaveLoading(false)
        }
      }
    },
  ]

  return (
    <Modal
      open={open}
      large
      onClose={() => {
        setOpen(false)
        setAddonModalStage(0)
      }}
      title="Create a New Addon"
      primaryAction={addonModalStage !== 2 ? {
        content: 'Save Addon',
        onAction: saveNewAddon,
        disabled: !newAddon || newAddon.price > 100000 || newAddon.title === '' || newAddon.description === '',
        loading: saveLoading,
      } : editPrimaryAction}
      secondaryActions={addonModalStage !== 2 ? [
        {
          content: 'Cancel',
          onAction: () => {
            setOpen(false)
            setAddonModalStage(0)
            setOpen(false);
          },
        },
      ] : editSecondaryActions}
    >
      <Modal.Section>
        {modalStageContent}
      </Modal.Section>
    </Modal>
  )
}
